// TechInfo.tsx
import React, { useState } from "react";
import { Loader2, CheckCircle2, AlertCircle } from "lucide-react";
import type { VCard } from "../../types/vcard";
import DeviceList from "./tech/DeviceList";
import OwnerSection from "./shared/OwnerSection";
import { useVCardProfileSync } from "../../hooks/useVCardProfileSync";
import useUniversalToggleWithAddress from "../../hooks/useUniversalToggleWithAddress";
import { getCategorySyncFields } from "../../constants/ownerFields";
import { DEFAULT_COUNTRY_CODE } from "../../constants/countryCodes";
import { useUserStore } from "../../store/userStore";
import type { UserProfile } from "../../types/user";

const MAX_FREE_DEVICES = 3;

interface TechInfoProps {
  formData: Partial<VCard>;
  handleChange: (
    e: React.ChangeEvent<
      HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement
    >,
  ) => void;
  handleSubmit: (e: React.FormEvent) => Promise<void>;
  isSubmitting?: boolean;
  showSaveConfirmation?: boolean;
  isActive: boolean;
}

const TechInfo: React.FC<TechInfoProps> = ({
  formData,
  handleChange,
  handleSubmit,
  isSubmitting = false,
  showSaveConfirmation = false,
  isActive,
}) => {
  void isActive;
  const [mobilePhoneCountryCode, setMobilePhoneCountryCode] =
    useState(DEFAULT_COUNTRY_CODE);
  const [workPhoneCountryCode, setWorkPhoneCountryCode] =
    useState(DEFAULT_COUNTRY_CODE);
  const [localShowSaveConfirmation, setLocalShowSaveConfirmation] =
    useState(false);
  const [error, setError] = useState<string | null>(null);
  const profile = useUserStore((state) => state.profile);
  const updateProfile = useUserStore((state) => state.updateProfile);

  const { vcard: techVCard } = useVCardProfileSync("tech", {
    syncFields: getCategorySyncFields("tech"),
    debounceMs: 300,
  });

  // Initialize our universal toggle hook for address update
  const {
    universalToggleWithAddress,
    isLoading: toggleLoading,
    error: toggleError,
  } = useUniversalToggleWithAddress();

  // Handler for toggling tech active state
  const handleToggle = async () => {
    try {
      await universalToggleWithAddress(techVCard?.id || "myTech");
    } catch (err) {
      console.error(err);
    }
  };

  // Use synced data if available
  const syncedFormData = {
    ...formData,
    firstName: techVCard?.firstName || formData.firstName,
    lastName: techVCard?.lastName || formData.lastName,
    email:
      techVCard?.email ||
      techVCard?.personalEmail ||
      formData.email ||
      formData.personalEmail,
    workEmail: techVCard?.workEmail || formData.workEmail,
    mobilePhone: techVCard?.mobilePhone || formData.mobilePhone,
    mobilePhoneCountryCode:
      techVCard?.mobilePhoneCountryCode || mobilePhoneCountryCode,
    workPhone: techVCard?.workPhone || formData.workPhone,
    workPhoneCountryCode:
      techVCard?.workPhoneCountryCode || workPhoneCountryCode,
    isActive: techVCard?.isActive || formData.isActive || false,
    categorySpecificInfo: {
      myTech: {
        devices:
          techVCard?.categorySpecificInfo?.myTech?.devices ||
          formData.categorySpecificInfo?.myTech?.devices ||
          [],
      },
    },
  };

  return (
    <form
      onSubmit={(e) => {
        e.preventDefault();
        handleSubmit(e)
          .then(async () => {
            if (profile) {
              const profileUpdates: Partial<UserProfile> = {};
              const formFirstName = (formData.firstName as string) || "";
              const formLastName = (formData.lastName as string) || "";
              const formEmail = (formData.email as string) || "";
              const formWorkEmail = (formData.workEmail as string) || "";
              const formMobilePhone = (formData.mobilePhone as string) || "";
              const formWorkPhone = (formData.workPhone as string) || "";
              const formTravelPhone = (formData.travelPhone as string) || "";

              if (formFirstName && formFirstName !== profile.firstName) {
                profileUpdates.firstName = formFirstName;
              }

              if (formLastName && formLastName !== profile.lastName) {
                profileUpdates.lastName = formLastName;
              }

              if (formEmail && formEmail !== profile.email) {
                profileUpdates.email = formEmail;
              }

              if (formWorkEmail !== (profile.alternativeEmail || "")) {
                profileUpdates.alternativeEmail = formWorkEmail;
              }

              if (formMobilePhone && formMobilePhone !== profile.phone) {
                profileUpdates.phone = formMobilePhone;
              }

              if (formWorkPhone !== (profile.workPhone || "")) {
                profileUpdates.workPhone = formWorkPhone;
              }

              if (formTravelPhone !== (profile.travelPhone || "")) {
                profileUpdates.travelPhone = formTravelPhone;
              }

              if (Object.keys(profileUpdates).length > 0) {
                await updateProfile(profileUpdates);
              }
            }

            setLocalShowSaveConfirmation(true);
            setTimeout(() => setLocalShowSaveConfirmation(false), 3000);
          })
          .catch((err) => {
            console.error("Error saving tech info:", err);
            setError("Failed to save tech information. Please try again.");
            setTimeout(() => setError(null), 5000);
          });
      }}
      className="space-y-6"
    >
      {error && (
        <div className="flex items-center space-x-2 p-4 bg-red-50 text-red-700 rounded-lg">
          <AlertCircle className="h-5 w-5 flex-shrink-0" />
          <span>{error}</span>
        </div>
      )}

      {(showSaveConfirmation || localShowSaveConfirmation) && (
        <div className="flex items-center space-x-2 p-4 bg-green-50 text-green-700 rounded-lg">
          <CheckCircle2 className="h-5 w-5" />
          <span>Changes saved successfully!</span>
        </div>
      )}

      {/* Active/Inactive Toggle */}
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center space-x-2">
          <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
            Tech Tag
          </h2>
          <span className="text-sm text-gray-500 dark:text-gray-400">
            (Defaults to Home when not Active)
          </span>
        </div>
        <label className="relative inline-flex items-center cursor-pointer">
          <input
            type="checkbox"
            className="sr-only peer"
            checked={syncedFormData.isActive}
            onChange={() => {
              void handleToggle();
            }}
          />
          <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-green-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-green-600"></div>
          <span className="ml-2 text-sm font-medium text-gray-900">
            {syncedFormData.isActive ? (
              <span className="text-green-600">Active</span>
            ) : (
              <span className="text-orange-500">Inactive</span>
            )}
          </span>
        </label>
      </div>

      {/* Owner Information */}
      <OwnerSection
        formData={syncedFormData}
        handleChange={handleChange}
        mobilePhoneCountryCode={mobilePhoneCountryCode}
        setMobilePhoneCountryCode={setMobilePhoneCountryCode}
        workPhoneCountryCode={workPhoneCountryCode}
        setWorkPhoneCountryCode={setWorkPhoneCountryCode}
      />

      {/* Device List */}
      <DeviceList
        formData={syncedFormData}
        handleChange={handleChange}
        maxDevices={MAX_FREE_DEVICES}
      />

      {/* Additional Notes */}
      <div>
        <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100 mb-4">
          Additional Notes
        </h3>
        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
            Notes
          </label>
          <textarea
            name="note"
            value={formData.note || ""}
            onChange={handleChange}
            rows={4}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-red-500"
            placeholder="Any additional information about your devices"
          />
        </div>
      </div>

      {/* Save Button */}
      <button
        type="submit"
        disabled={isSubmitting || toggleLoading}
        className="w-full bg-red-600 text-white py-2 px-4 rounded-lg hover:bg-red-700 transition-colors disabled:bg-red-400 disabled:cursor-not-allowed"
      >
        {isSubmitting || toggleLoading ? (
          <div className="flex items-center justify-center">
            <Loader2 className="h-5 w-5 animate-spin mr-2" />
            <span>Saving...</span>
          </div>
        ) : (
          "Save MyTech Details"
        )}
      </button>

      {toggleError && (
        <p className="mt-4 text-red-600">Toggle Error: {toggleError}</p>
      )}
    </form>
  );
};

export default TechInfo;
